import { Fieldset as BaseFieldset } from "@base-ui/react/fieldset";
import clsx from "clsx";
import PropTypes from "prop-types";
import * as React from "react";
import useDomId from "../../utils/useDomId";
import css from "./Fieldset.module.css";

const DescriptionContext = React.createContext(null);

const Root = React.forwardRef(
  ({ disabled = false, className, children, ...rest }, ref) => {
    const descriptionId = useDomId("fieldset-description");
    const [hasDescription, setHasDescription] = React.useState(false);
    const description = React.useMemo(
      () => ({ id: descriptionId, setHasDescription }),
      [descriptionId],
    );

    return (
      <DescriptionContext.Provider value={description}>
        <BaseFieldset.Root
          ref={ref}
          className={clsx(css.root, className)}
          disabled={disabled}
          aria-describedby={hasDescription ? descriptionId : undefined}
          {...rest}
        >
          {children}
        </BaseFieldset.Root>
      </DescriptionContext.Provider>
    );
  },
);

Root.displayName = "FieldsetRoot";

Root.propTypes = {
  disabled: PropTypes.bool,
  render: PropTypes.element,
  className: PropTypes.string,
  children: PropTypes.node,
};

const Legend = React.forwardRef(({ className, children, ...rest }, ref) => (
  <BaseFieldset.Legend
    ref={ref}
    className={clsx(css.legend, className)}
    {...rest}
  >
    {children}
  </BaseFieldset.Legend>
));

Legend.displayName = "FieldsetLegend";

Legend.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const Description = React.forwardRef(
  ({ className, children, ...rest }, ref) => {
    const { id, setHasDescription } = React.useContext(DescriptionContext);

    React.useEffect(() => {
      setHasDescription(true);
      return () => setHasDescription(false);
    }, [setHasDescription]);

    return (
      <p
        ref={ref}
        id={id}
        className={clsx(css.description, className)}
        {...rest}
      >
        {children}
      </p>
    );
  },
);

Description.displayName = "FieldsetDescription";

Description.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
};

const Fieldset = { Root, Legend, Description };

export default Fieldset;
